/**
 * Calculates the min and max values from an array using a value accessor.
 *
 * @param data - Array of data items
 * @param getValue - Function to extract numeric value from each item
 * @param defaultRange - Default range if data is empty or has no valid values
 * @returns Object with min and max values
 *
 * @example
 * ```ts
 * const range = calculateValueRange(items, (item) => item.price);
 * // { min: 10, max: 100 }
 * ```
 */
const calculateValueRange = <T>(
  data: T[],
  getValue: (item: T) => number,
  defaultRange = { min: 0, max: 1 },
): { min: number; max: number } => {
  if (data.length === 0) return defaultRange;

  // Extract numeric values, filtering out undefined or NaN
  const values = data.map(getValue).filter((v) => v !== undefined && !Number.isNaN(v));

  if (values.length === 0) return defaultRange;

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
};

/**
 * Calculates "nice" round boundaries for a given range.
 * Useful for generating filter options, chart axis ticks, or legend steps.
 *
 * The algorithm finds human-friendly step sizes (1, 2, 5, 10, 20, 50, etc.)
 * and generates evenly spaced boundaries within the given range.
 *
 * @param min - Minimum value of the range
 * @param max - Maximum value of the range
 * @param buckets - Number of divisions/buckets to create (default: 4)
 * @returns Array of nice round boundary values
 *
 * @example
 * ```ts
 * calculateNiceBoundaries(3.2, 18.7, 4);
 * // [4, 8, 12, 16]
 *
 * calculateNiceBoundaries(0.15, 0.89, 4);
 * // [0.2, 0.4, 0.6, 0.8]
 * ```
 */
const calculateNiceBoundaries = (min: number, max: number, buckets = 4): number[] => {
  const range = max - min;

  // Edge case: no range or invalid range, return single boundary
  if (range <= 0) return [min];

  // Step 1: Calculate the raw step size based on desired bucket count
  // e.g., range=15.5, buckets=4 → rawStep=3.875
  const rawStep = range / buckets;

  // Step 2: Find the order of magnitude (power of 10) of the raw step
  // e.g., rawStep=3.875 → magnitude=1 (10^0), rawStep=0.185 → magnitude=0.1 (10^-1)
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));

  // Step 3: Normalize the raw step to a value between 1 and 10
  // e.g., rawStep=3.875, magnitude=1 → normalized=3.875
  const normalized = rawStep / magnitude;

  // Step 4: Round to the nearest "nice" multiplier (1, 2, 5, or 10)
  // This ensures boundaries are human-readable numbers
  // e.g., normalized=3.875 → multiplier=5 → niceStep=5
  const niceStep = magnitude * (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10);

  // Step 5: Find the first "nice" number at or above the minimum
  // e.g., min=3.2, niceStep=5 → niceMin=5
  const niceMin = Math.ceil(min / niceStep) * niceStep;

  // Step 6: Calculate how many boundaries fit within the range
  // e.g., max=18.7, niceMin=5, niceStep=5 → count=3 (values: 5, 10, 15)
  const count = Math.floor((max - niceMin) / niceStep) + 1;

  // Edge case: if no boundaries fit, return the starting point
  if (count <= 0) return [niceMin];

  // Step 7: Generate the array of boundaries
  // e.g., [5, 10, 15] for niceMin=5, niceStep=5, count=3
  return Array.from({ length: count }, (_, i) => niceMin + i * niceStep);
};

export { calculateNiceBoundaries, calculateValueRange };
